import Konva from 'konva';
import type { ComponentInstance } from '../../core/model/component.js';
import type { ComponentDef } from '../../core/library/component-def.js';
import { renderPin, renderBendMarks } from './pin-renderer.js';
import { renderResistorBody } from './resistor-renderer.js';
import { renderDipBody } from './dip-renderer.js';
import { renderCeramicDiscBody } from './capacitor-renderer.js';
import { renderElectrolyticBody } from './electrolytic-renderer.js';
import { renderTo92Body } from './to92-renderer.js';
import { renderTo126Body } from './to126-renderer.js';
import { renderDiodeBody } from './diode-renderer.js';
import { renderLedBody } from './led-renderer.js';
import { renderBattery9VBody } from './battery-renderer.js';
import { renderPushButtonBody } from './push-button-renderer.js';
import { renderSevenSegmentBody } from './seven-segment-renderer.js';
import { getInstanceProperty } from '../../core/library/component-def.js';

/**
 * Renderiza una instancia de componente completa: cuerpo + pines + marcas.
 * Las marcas de doblez SOLO se dibujan si `showBendMarks` es true
 * (típicamente cuando el componente está seleccionado).
 */
export function renderComponent(
  instance: ComponentInstance,
  def: ComponentDef,
  showBendMarks = false,
): Konva.Group {
  const group = new Konva.Group({
    x: instance.position_mm.x,
    y: instance.position_mm.y,
    rotation: instance.rotation_deg,
    scaleY: instance.flipped ? -1 : 1,
    listening: true,
  });
  group.setAttr('componentUid', instance.uid);

  // Pines primero.
  const metalColor = def.appearance.metal_color ?? '#c8c8c8';
  const isDip = def.body.shape === 'dip';
  for (let i = 0; i < def.pins.length; i++) {
    const pinDef = def.pins[i]!;
    const pinState = instance.pins[i];
    if (!pinState) continue;
    const pinGroup = renderPin(pinDef, pinState, {
      metalColor,
      flipped: instance.flipped ?? false,
      rotationDeg: instance.rotation_deg,
      pinNumber: isDip ? pinDef.id : '',
    });
    group.add(pinGroup);
  }

  // Cuerpo.
  const body = renderBody(def, instance);
  group.add(body);

  // Marcas de dobleces por encima del cuerpo. Solo si el componente
  // está seleccionado y el pin es editable.
  if (showBendMarks) {
    for (let i = 0; i < def.pins.length; i++) {
      const pinDef = def.pins[i]!;
      if (pinDef.locked) continue;
      const pinState = instance.pins[i];
      if (!pinState) continue;
      // Tamaño unificado de las marcas de doblez en todos los
      // componentes (referencia: pines de TO-126 = 1.4 mm de diámetro
      // con el factor 0.5 anterior).
      const marks = renderBendMarks(pinDef, pinState, {
        tickHalfMm: 0.63,
        tickStrokeMm: 0.10,
      });
      group.add(marks);
    }
  }

  return group;
}

function renderBody(def: ComponentDef, instance: ComponentInstance): Konva.Group {
  const labelText = instance.value ?? def.electrical?.default_value ?? def.appearance.label?.text ?? '';
  const rot = instance.rotation_deg;
  const flipped = instance.flipped ?? false;
  switch (def.body.shape) {
    case 'cylinder-axial':
      return renderResistorBody(def, labelText, rot, flipped);
    case 'dip':
      return renderDipBody(def, labelText, flipped, rot);
    case 'disc-radial':
      return renderCeramicDiscBody(def, labelText, rot, flipped);
    case 'cylinder-radial':
      return renderElectrolyticBody(def, labelText, rot, flipped);
    case 'diode-axial':
      return renderDiodeBody(def, labelText, rot, flipped);
    case 'to92': {
      const pinout = getInstanceProperty(instance.properties, def, 'pinout');
      return renderTo92Body(def, labelText, rot, flipped, pinout ? pinout.split('') : []);
    }
    case 'to126':
    case 'to220': {
      const pinout = getInstanceProperty(instance.properties, def, 'pinout');
      return renderTo126Body(def, labelText, rot, flipped, pinout ? pinout.split('') : []);
    }
    case 'led-round': {
      const color = getInstanceProperty(instance.properties, def, 'color') || 'green';
      return renderLedBody(def, labelText, color, rot, flipped);
    }
    case 'battery-9v':
      return renderBattery9VBody(def, labelText, rot, flipped);
    case 'push-button':
      return renderPushButtonBody(def, labelText, rot, flipped);
    case 'seven-segment':
      return renderSevenSegmentBody(def, labelText, rot, flipped);
    default: {
      const g = new Konva.Group();
      const w = def.body.length_mm ?? def.body.width_mm ?? 5;
      const h = def.body.diameter_mm ?? def.body.height_mm ?? 5;
      const rect = new Konva.Rect({
        x: -w / 2,
        y: -h / 2,
        width: w,
        height: h,
        fill: def.appearance.body_color,
        stroke: '#666',
        strokeWidth: 0.1,
        cornerRadius: 0.5,
        listening: true,
      });
      rect.setAttr('nodeKind', 'body');
      g.add(rect);
      return g;
    }
  }
}
